import React, { useState, useEffect } from 'react';
import { ptzApi, presetApi } from '../../services/api';
import { useAppStore } from '../../store/appStore';

export default function SyncPresetsModal({ camera, localPresets = [], onClose, onSynced }) {
  const { addToast } = useAppStore();
  const [loading, setLoading] = useState(true);
  const [cameraPresets, setCameraPresets] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const presetsApi = ptzApi;
    presetsApi.ptzPresets(camera.id)
      .then(res => {
        setCameraPresets(res.data);
        setSelected(new Set(res.data.map(p => p.id)));
      })
      .catch(err => {
        setError(err.response?.data?.error || err.message || 'Failed to fetch presets from camera');
      })
      .finally(() => setLoading(false));
  }, [camera.id]);

  function toggle(id) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  async function sync() {
    const cameraSync = cameraPresets
      .filter(p => selected.has(p.id))
      .map(p => ({ id: p.id, name: p.name, displayName: p.name, source: 'camera', cameraPresetId: p.id }));
    // Preserve any locally-saved (axisvms) presets — don't wipe them on camera sync
    const merged = [...cameraSync, ...localPresets];
    setSaving(true);
    try {
      await presetApi.save(camera.id, merged);
      addToast({ type: 'success', message: `Synced ${cameraSync.length} camera preset(s)` });
      onSynced();
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Failed to save presets';
      addToast({ type: 'error', message: `Sync failed: ${msg}` });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: '480px' }}>
        <div className="modal-header">
          <h2>Sync Presets from Camera</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '22px', lineHeight: 1 }}>×</button>
        </div>

        <div className="modal-body">
          {loading && (
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', fontSize: '12px' }}>
              Fetching presets from camera...
            </div>
          )}
          {error && (
            <div style={{ color: 'var(--accent-red)', fontFamily: 'var(--font-mono)', fontSize: '12px', padding: '12px', background: 'rgba(255,51,102,0.06)', borderRadius: 'var(--radius-md)' }}>
              {error}<br />
              <span style={{ color: 'var(--text-muted)', fontSize: '11px' }}>Check PM2 logs for the raw camera response.</span>
            </div>
          )}
          {!loading && !error && cameraPresets.length === 0 && (
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', fontSize: '12px' }}>
              No presets found on this camera.<br />
              <span style={{ fontSize: '11px' }}>Check PM2 logs: <code>pm2 logs axisvms-backend --lines 50</code></span>
            </div>
          )}
          {!loading && cameraPresets.length > 0 && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                <button className="btn btn-ghost btn-sm" onClick={() => setSelected(new Set(cameraPresets.map(p => p.id)))}>Select All</button>
                <button className="btn btn-ghost btn-sm" onClick={() => setSelected(new Set())}>Deselect All</button>
                <span style={{ marginLeft: 'auto', fontFamily: 'var(--font-mono)', fontSize: '11px', color: 'var(--text-muted)' }}>
                  {selected.size} / {cameraPresets.length}
                </span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', maxHeight: '340px', overflowY: 'auto' }}>
                {cameraPresets.map(preset => (
                  <label
                    key={preset.id}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '12px',
                      padding: '10px 12px',
                      background: selected.has(preset.id) ? 'rgba(244,123,32,0.07)' : 'var(--bg-surface)',
                      border: `1px solid ${selected.has(preset.id) ? 'var(--accent-orange-dim)' : 'var(--border-dim)'}`,
                      borderRadius: 'var(--radius-md)',
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selected.has(preset.id)}
                      onChange={() => toggle(preset.id)}
                      style={{ accentColor: 'var(--accent-orange)', width: '15px', height: '15px', cursor: 'pointer', flexShrink: 0 }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontFamily: 'var(--font-display)', fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)' }}>
                        {preset.name}
                      </div>
                      <div style={{ fontFamily: 'var(--font-mono)', fontSize: '10px', color: 'var(--text-muted)' }}>
                        Preset #{preset.id}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button
            className="btn btn-primary"
            onClick={sync}
            disabled={saving || selected.size === 0 || loading}
          >
            {saving ? 'Syncing...' : `Sync Selected (${selected.size})`}
          </button>
        </div>
      </div>
    </div>
  );
}
